package fr.manyverse.app.infrastructure.datastore

import fr.manyverse.app.infrastructure.serialization.appJson
import kotlinx.browser.localStorage
import org.w3c.dom.get
import org.w3c.dom.set

/**
 * localStorage-based implementation of SoundMetadataDataStore for JS platform.
 * Sound metadata is small enough to fit in localStorage (5MB limit).
 * Audio files are stored separately in IndexedDB.
 */
class JsSoundMetadataDataStore : SoundMetadataDataStore {

    companion object {
        private const val SOUNDS_KEY = "sounds_metadata"
    }

    private val json = appJson { ignoreUnknownKeys = true }

    override suspend fun saveSoundMetadata(metadata: SoundMetadata): Result<Unit> = runCatching {
        val currentList = getStoredSounds().toMutableList()
        // Remove existing entry with same ID if present
        currentList.removeAll { it.id == metadata.id }
        currentList.add(metadata)
        localStorage[SOUNDS_KEY] = json.encodeToString(currentList)
    }

    override suspend fun getSoundMetadata(soundId: String): SoundMetadata? {
        return getStoredSounds().find { it.id == soundId }
    }

    override suspend fun getAllSoundsForUser(userId: String): List<SoundMetadata> {
        return getStoredSounds().filter { it.userId == userId }
    }

    override suspend fun getSoundsByUserAndUniverse(
        userId: String,
        universeId: String
    ): List<SoundMetadata> {
        return getStoredSounds().filter {
            it.userId == userId && it.universeId == universeId
        }
    }

    override suspend fun deleteSoundMetadata(soundId: String): Result<Unit> = runCatching {
        val currentList = getStoredSounds().toMutableList()
        currentList.removeAll { it.id == soundId }
        localStorage[SOUNDS_KEY] = json.encodeToString(currentList)
    }

    /**
     * Une liste illisible lève une exception au lieu de passer pour vide : l'enregistrement suivant
     * écraserait sinon tous les sons de l'utilisateur. `LocalSoundRepository` remonte l'échec.
     */
    private fun getStoredSounds(): List<SoundMetadata> {
        val jsonString = localStorage[SOUNDS_KEY] ?: return emptyList()
        return json.decodeFromString<List<SoundMetadata>>(jsonString)
    }
}
